import { Button, Flex, Form, Input, Switch } from 'antd';
import { useEffect } from 'react';
import {
  useCreateDesignationMutation,
  useUpdateDesignationMutation,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import useAuth from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';

type Props = CustomModalProps & {
  id?: string;
  data?: any;
  type?: string;
};

const ManageDesignation = ({ id, open, handleClose, data }: Props) => {
  const [form] = Form.useForm();
  const { organizationId } = useAuth();

  // API hooks
  const [createDesignation, { isLoading: creating }] =
    useCreateDesignationMutation();
  const [updateDesignation, { isLoading: updating }] =
    useUpdateDesignationMutation();

  const handleSubmit = async (values: any) => {
    try {
      if (isEditMode && id) {
        await updateDesignation({
          data: {
            name: values.name,
            status: values?.status ? 'active' : 'inactive',
          },
          id,
        }).unwrap();
        toast('success', 'Designation updated successfully');
      } else {
        await createDesignation({
          name: values.name,
          organization_id: organizationId,
          status: values?.status ? 'active' : 'inactive',
        }).unwrap();
        toast('success', 'Designation created successfully');
      }

      handleClose();
      form.resetFields();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to save designation');
      toast('error', errorMessage);
    }
  };

  const isEditMode = !!id;

  // Populate form when editing
  useEffect(() => {
    if (data && open) {
      form.setFieldsValue({
        name: data.name,
        status: (data as any).status === 'active' ? true : false,
      });
    }
  }, [data, form, open]);

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Edit Designation' : 'Add New Designation'}
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        className="custom-form space-y-6"
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Designation Name"
          name="name"
          rules={[{ required: true, message: 'Please enter designation name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          layout="horizontal"
          initialValue={true}
          className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
        >
          <Switch className="custom-switch" />
        </Form.Item>

        <Flex justify="end">
          <Button
            className="btn-primary min-w-[80px] h-11 font-semibold"
            htmlType="submit"
            loading={creating || updating}
          >
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Flex>
      </Form>
    </CustomDrawer>
  );
};

export default ManageDesignation;
